import React, { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';

import { hasWebcam } from 'detectrtc';
import { MenuItem, MenuSurfaceAnchor } from '@rmwc/menu';

import useMatchMedia from '../../../hooks/useMatchMedia';
import useRTCDetect from '../../../hooks/useRTCDetect';
import { isIOS } from '../../../utils';

import StdDropdown from '../StdDropdown';
import StdButton from '../StdButton';
import Camera from '../../misc/Camera';

import './filepicker.scss';
import StdIcon from '../StdIcon';

interface IProps extends IClass {
    title: string;
    accept?: string;
    enableWebcam?: boolean;
    capture?: boolean | string;
    disabled?: boolean;
    backgroundColor?: string;
    onFileUpload?: (files: File[]) => void;
}

const StdFilepicker: React.FC<IProps> = ({
    className,
    backgroundColor,
    title,
    accept = 'image/*',
    enableWebcam,
    capture,
    disabled,
    onFileUpload
}) => {
    const { t } = useTranslation();
    const [cameraOpen, setCameraOpen] = useState(false);
    const [optionsOpen, setOptionsOpen] = useState(false);
    const isDesktop = useMatchMedia('desktop');

    const haveChecksLoaded = useRTCDetect();
    const isDisabled = !haveChecksLoaded || disabled;

    // Desktop uses the custom getUserMedia based <Camera> overlay. `isDesktop`
    // is a pure CSS viewport-width check ((min-width: 1025px)) — an iPad in
    // landscape (~1180px) exceeds that width and would otherwise be routed
    // here too. `isIOS` also catches "MacIntel" + multi-touch (iPad Safari's
    // default UA since iPadOS 13), so we explicitly exclude it: our own
    // MediaRecorder-based video capture doesn't produce a playable file on
    // WebKit (ASW-1485), whereas the native mobile camera path below always
    // does. Doesn't affect MS Surface (navigator.platform isn't MacIntel)
    // or real Macs (maxTouchPoints is 0).
    const hasWebcamOption =
        enableWebcam && isDesktop && !isIOS && haveChecksLoaded && hasWebcam;

    // On mobile we rely on the native file input `capture` attribute so the OS
    // opens the camera directly. iOS Safari only shows the "Take Photo" option
    // when the input is NOT `multiple`, so we render a dedicated capture input.
    // Also covers iPad-in-desktop-mode, excluded from hasWebcamOption above.
    const hasNativeCameraOption = enableWebcam && (!isDesktop || isIOS);

    const onOptionSelect = useCallback((event: ISelectEvent) => {
        const { value } = event.detail.item.dataset;
        if (value === 'use_camera') setCameraOpen(true);
    }, []);

    const onFileSelected = useCallback(
        (event: React.ChangeEvent<HTMLInputElement>) => {
            const { files } = event.target;
            if (!files) return;

            onFileUpload?.(Array.from(files));

            // Reset so selecting the same file again re-triggers onChange.
            event.target.value = '';
        },
        [onFileUpload]
    );

    const onMediaCreated = useCallback(
        (file: File) => {
            onFileUpload?.([file]);
        },
        [onFileUpload]
    );

    // Gallery / file browser input. `multiple` allows selecting several files
    // at once but on iOS it disables the direct camera capture, so this input
    // is only used for browsing existing files.
    const browseInputNode = (
        <input
            className="absolute-input"
            type="file"
            accept={accept}
            onChange={onFileSelected}
            multiple
        />
    );

    // Dedicated camera input. `capture` opens the camera directly on mobile.
    // It must NOT have `multiple`, otherwise iOS Safari hides the camera option.
    // Android Chrome only launches the camera directly when `accept` names a
    // SINGLE media type — a combined "image/*,video/*" falls back to the
    // gallery picker (ASW-1483). So each capture input gets exactly one type.
    const cameraInputNode = (
        <input
            className="absolute-input"
            type="file"
            accept="image/*"
            onChange={onFileSelected}
            capture={capture ?? 'environment'}
        />
    );

    // Whether the caller allows video files at all (e.g. protocol media does,
    // attachments/logos don't) — controls the "Record video" option below.
    const acceptsVideo = accept.includes('video');

    // Dedicated video capture input — same single-type rule as the camera
    // input: `video/*` + `capture`, no `multiple` (ASW-1483).
    const videoInputNode = (
        <input
            className="absolute-input"
            type="file"
            accept="video/*"
            onChange={onFileSelected}
            capture={capture ?? 'environment'}
        />
    );


    // Single input used on platforms where we don't split camera/browse.
    const inputNode = (
        <input
            className="absolute-input"
            type="file"
            accept={accept}
            onChange={onFileSelected}
            capture={capture}
            multiple
        />
    );

    const webcamUploadNote = (
        <div className="file-picker__anchor">
            <MenuSurfaceAnchor>
                <StdDropdown
                    open={optionsOpen}
                    onClose={() => setOptionsOpen(false)}
                    onSelect={onOptionSelect}
                >
                    <MenuItem data-value="use_camera">
                        {t('filepicker.use_webcam')}
                    </MenuItem>
                    <MenuItem className="file-picker__trigger">
                        <span>{t('filepicker.browse_files')}</span>
                        {browseInputNode}
                    </MenuItem>
                </StdDropdown>

                <button
                    className="file-picker__add"
                    onClick={() => setOptionsOpen(true)}
                    disabled={isDisabled}
                >
                    <StdIcon className="file-picker__add-icon" name="plus" />
                    <p className="file-picker__add-text">{title}</p>
                </button>
            </MenuSurfaceAnchor>
            <Camera
                open={cameraOpen}
                onClose={() => setCameraOpen(false)}
                onFileCreated={onMediaCreated}
            />
        </div>
    );

    // Mobile: offer "Take photo" / "Record video" (native camera) and
    // "Browse files". Video only when the caller accepts video files.
    const mobileUploadNode = (
        <div className="file-picker__anchor">
            <MenuSurfaceAnchor>
                <StdDropdown
                    open={optionsOpen}
                    onClose={() => setOptionsOpen(false)}
                >
                    <MenuItem className="file-picker__trigger">
                        <span>{t('filepicker.take_photo')}</span>
                        {cameraInputNode}
                    </MenuItem>
                    {acceptsVideo && (
                        <MenuItem className="file-picker__trigger">
                            <span>{t('filepicker.record_video')}</span>
                            {videoInputNode}
                        </MenuItem>
                    )}
                    <MenuItem className="file-picker__trigger">
                        <span>{t('filepicker.browse_files')}</span>
                        {browseInputNode}
                    </MenuItem>
                </StdDropdown>

                <button
                    className="file-picker__add"
                    onClick={() => setOptionsOpen(true)}
                    disabled={isDisabled}
                >
                    <StdIcon className="file-picker__add-icon" name="plus" />
                    <p className="file-picker__add-text">{title}</p>
                </button>
            </MenuSurfaceAnchor>
        </div>
    );

    const defaultUploadNode = (
        <button
            className={`file-picker__add file-picker__bg-${backgroundColor}`}
            onClick={() => setOptionsOpen(true)}
            disabled={isDisabled}
        >
            <StdIcon className="file-picker__add-icon" name="plus" />
            <p className="file-picker__add-text">{title}</p>
            {haveChecksLoaded && inputNode}
        </button>
    );

    if (hasWebcamOption) return webcamUploadNote;
    if (hasNativeCameraOption) return mobileUploadNode;
    return defaultUploadNode;
};

export default StdFilepicker;
